import React, { useEffect, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { format, parseISO } from 'date-fns';
import { de } from 'date-fns/locale';
import { useNavigate } from 'react-router-dom';
import {
    Clock,
    CheckSquare,
    Calendar as CalendarIcon,
    FileEdit,
    Trash2,
    Activity as ActivityIcon,
    Building2,
    ExternalLink,
    Loader2,
} from 'lucide-react';

const KIND_META = {
    time_entry: { icon: Clock, label: 'Zeit', color: 'text-emerald-600 bg-emerald-500/10' },
    task_created: { icon: CheckSquare, label: 'Aufgabe', color: 'text-blue-600 bg-blue-500/10' },
    calendar_event: { icon: CalendarIcon, label: 'Termin', color: 'text-purple-600 bg-purple-500/10' },
    audit: { icon: FileEdit, label: 'Aktion', color: 'text-slate-600 bg-slate-500/10' },
};

const groupByDay = (items) => {
    const groups = {};
    items.forEach(it => {
        if (!it.when) return;
        try {
            const d = parseISO(it.when);
            const key = format(d, 'yyyy-MM-dd');
            if (!groups[key]) groups[key] = [];
            groups[key].push(it);
        } catch (_) { /* skip */ }
    });
    return Object.entries(groups).sort((a, b) => b[0].localeCompare(a[0]));
};

const ActivityItem = ({ item, onJump }) => {
    const meta = KIND_META[item.kind] || KIND_META.audit;
    const Icon = item.kind === 'audit' && item.meta?.action === 'delete' ? Trash2 : meta.icon;
    let timeLabel = '';
    if (item.when) {
        try { timeLabel = format(parseISO(item.when), 'HH:mm', { locale: de }); } catch (_) { /* */ }
    }
    return (
        <div className="flex gap-3 py-3 group" data-testid={`activity-item-${item.kind}`}>
            <div className="flex flex-col items-center pt-0.5">
                <div className={`w-9 h-9 rounded-xl flex items-center justify-center ${meta.color}`}>
                    <Icon className="w-4 h-4" />
                </div>
            </div>
            <div className="flex-1 min-w-0">
                <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                            <span className="font-medium truncate">{item.title}</span>
                            {item.meta?.duration && (
                                <Badge variant="secondary" className="text-[10px] font-mono">
                                    {item.meta.duration}
                                </Badge>
                            )}
                            {item.meta?.is_pause && (
                                <Badge variant="outline" className="text-[10px]">Pause</Badge>
                            )}
                            {item.meta?.is_edited && (
                                <Badge variant="outline" className="text-[10px]">bearbeitet</Badge>
                            )}
                        </div>
                        {item.subtitle && (
                            <p className="text-xs text-muted-foreground mt-0.5 line-clamp-2">{item.subtitle}</p>
                        )}
                        {item.customer_name && item.kind !== 'time_entry' && (
                            <div className="flex items-center gap-1 mt-1 text-xs text-primary">
                                <Building2 className="w-3 h-3" />
                                {item.customer_name}
                            </div>
                        )}
                    </div>
                    <div className="flex flex-col items-end gap-1 shrink-0">
                        <span className="text-xs text-muted-foreground tabular-nums">{timeLabel}</span>
                        {item.link && (
                            <button
                                onClick={() => onJump(item.link)}
                                className="opacity-0 group-hover:opacity-100 transition-opacity text-xs text-primary hover:underline flex items-center gap-1"
                                data-testid="activity-jump"
                            >
                                Öffnen <ExternalLink className="w-3 h-3" />
                            </button>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

const MyActivityPage = () => {
    const { api, user } = useAuth();
    const navigate = useNavigate();
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [kindFilter, setKindFilter] = useState('all');
    const [daysFilter, setDaysFilter] = useState('30');

    useEffect(() => {
        let cancelled = false;
        const load = async () => {
            setLoading(true);
            try {
                const res = await api.get(`/my-activity?days=${daysFilter}&limit=200`);
                if (!cancelled) setItems(res.data.items || []);
            } catch (_) {
                if (!cancelled) setItems([]);
            } finally {
                if (!cancelled) setLoading(false);
            }
        };
        load();
        return () => { cancelled = true; };
    }, [api, daysFilter]);

    const filtered = items.filter(it => kindFilter === 'all' || it.kind === kindFilter);
    const grouped = groupByDay(filtered);

    // Quick stats
    const todayKey = format(new Date(), 'yyyy-MM-dd');
    const todayItems = filtered.filter(it => it.when && it.when.startsWith(todayKey));
    const totalDurationToday = todayItems
        .filter(it => it.kind === 'time_entry' && !it.meta?.is_pause)
        .reduce((acc, it) => acc + (it.meta?.duration_seconds || 0), 0);

    const fmtDur = (sec) => {
        if (!sec) return '0h 0m';
        const h = Math.floor(sec / 3600);
        const m = Math.floor((sec % 3600) / 60);
        return `${h}h ${m}m`;
    };

    return (
        <div className="max-w-3xl mx-auto p-4 sm:p-6 space-y-5" data-testid="my-activity-page">
            <div>
                <h1 className="text-2xl font-bold flex items-center gap-2">
                    <ActivityIcon className="w-6 h-6 text-primary" />
                    Meine Aktivität
                </h1>
                <p className="text-sm text-muted-foreground mt-1">
                    Hier siehst du alles, was du selbst eingetragen oder geändert hast — Zeiteinträge, Aufgaben, Termine.
                </p>
            </div>

            {/* Quick stats */}
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                <Card className="p-3">
                    <div className="text-xs text-muted-foreground">Heute</div>
                    <div className="text-xl font-bold mt-0.5" data-testid="stat-today-count">{todayItems.length}</div>
                    <div className="text-[10px] text-muted-foreground">Aktivitäten</div>
                </Card>
                <Card className="p-3">
                    <div className="text-xs text-muted-foreground">Heute getrackt</div>
                    <div className="text-xl font-bold mt-0.5 font-mono" data-testid="stat-today-duration">{fmtDur(totalDurationToday)}</div>
                    <div className="text-[10px] text-muted-foreground">netto, ohne Pausen</div>
                </Card>
                <Card className="p-3 col-span-2 sm:col-span-1">
                    <div className="text-xs text-muted-foreground">Gesamt im Zeitraum</div>
                    <div className="text-xl font-bold mt-0.5" data-testid="stat-total">{filtered.length}</div>
                    <div className="text-[10px] text-muted-foreground">Einträge</div>
                </Card>
            </div>

            {/* Filters */}
            <div className="flex flex-wrap gap-2">
                <Select value={kindFilter} onValueChange={setKindFilter}>
                    <SelectTrigger className="w-full sm:w-44" data-testid="kind-filter">
                        <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                        <SelectItem value="all">Alle Aktivitäten</SelectItem>
                        <SelectItem value="time_entry">Nur Zeiteinträge</SelectItem>
                        <SelectItem value="task_created">Nur Aufgaben</SelectItem>
                        <SelectItem value="calendar_event">Nur Termine</SelectItem>
                        <SelectItem value="audit">Nur Änderungen</SelectItem>
                    </SelectContent>
                </Select>
                <Select value={daysFilter} onValueChange={setDaysFilter}>
                    <SelectTrigger className="w-full sm:w-44" data-testid="days-filter">
                        <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                        <SelectItem value="7">Letzte 7 Tage</SelectItem>
                        <SelectItem value="30">Letzte 30 Tage</SelectItem>
                        <SelectItem value="90">Letzte 90 Tage</SelectItem>
                        <SelectItem value="365">Letztes Jahr</SelectItem>
                    </SelectContent>
                </Select>
            </div>

            {loading ? (
                <Card className="p-12 flex items-center justify-center">
                    <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
                </Card>
            ) : grouped.length === 0 ? (
                <Card className="p-12 text-center text-muted-foreground">
                    <ActivityIcon className="w-10 h-10 mx-auto mb-3 opacity-40" />
                    <p>Noch keine Aktivität im gewählten Zeitraum.</p>
                </Card>
            ) : (
                <div className="space-y-5">
                    {grouped.map(([dayKey, dayItems]) => {
                        const label = format(parseISO(dayKey + 'T00:00:00'), 'EEEE, d. MMMM yyyy', { locale: de });
                        const isToday = dayKey === todayKey;
                        return (
                            <Card key={dayKey} className="overflow-hidden">
                                <div className="px-4 py-2 bg-muted/40 border-b flex items-center justify-between">
                                    <div className="font-semibold text-sm">
                                        {isToday ? 'Heute' : label}
                                        {isToday && <span className="ml-2 text-xs text-muted-foreground font-normal">· {label}</span>}
                                    </div>
                                    <span className="text-xs text-muted-foreground">{dayItems.length} Einträge</span>
                                </div>
                                <div className="px-4 divide-y divide-border/40">
                                    {dayItems.map(item => (
                                        <ActivityItem key={item.id} item={item} onJump={(p) => navigate(p)} />
                                    ))}
                                </div>
                            </Card>
                        );
                    })}
                </div>
            )}
        </div>
    );
};

export default MyActivityPage;
